// The kit's primitives: what a clean screen (eslint.config.js, CLEAN_SCREENS)
// draws with instead of Mantine, Tabler and inline styles. LOOKS.md Part 7,
// MODULARITY.md stage 4.
//
// DEFINED BY WHAT OUR SCREENS NEED, NOT BY WHAT MANTINE OFFERS (ICD U-02's
// rule). Each takes the few choices a screen makes — a tone, a size, a
// variant — and never a raw value, so a look restyles all of them from its
// stylesheet and another component library replaces Mantine here without a
// screen changing. Grown by what the trade screens needed first
// (PLAN-2026-09-26-trades T6); the next clean screen adds what it needs.
//
// A NAMED TONE, NEVER A COLOR. `tone="bad"` is a refusal or a veto, `good` a
// done deal, `warn` something waiting on somebody; which color each is belongs
// to the palette and the look.

import {
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type Ref,
  type SyntheticEvent,
} from 'react';
import {
  ActionIcon,
  Badge,
  Button as MantineButton,
  Checkbox as MantineCheckbox,
  Chip as MantineChip,
  ColorInput,
  Modal,
  PasswordInput,
  MultiSelect,
  NumberInput,
  SegmentedControl,
  Select,
  Slider,
  Switch,
  TextInput,
  Textarea,
  type MantineColor,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import { BackCloses } from './backCloses';
import { Icon, type IconName } from './Icon';
import type { Face } from '@fsp/domain';
import { FaceArt } from './FaceArt';

/** A step of the spacing scale (`--fsp-space-*`, tokens.css). */
export type Space = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type Tone = 'neutral' | 'accent' | 'good' | 'warn' | 'bad';

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(' ');

// ── Layout ──────────────────────────────────────────────────────────────────

/** Children in a column, `gap` apart. */
export function Stack({
  gap = 3,
  children,
  className,
}: {
  gap?: Space;
  children: ReactNode;
  className?: string;
}) {
  return <div className={cx('fsp-ui-stack', `fsp-ui-gap-${gap}`, className)}>{children}</div>;
}

/**
 * Children in a line, `gap` apart; `spread` pushes the last one to the end,
 * and `end` sets them all at the end: a dialog's buttons.
 */
export function Line({
  gap = 2,
  align = 'center',
  spread = false,
  end = false,
  wrap = false,
  children,
  className,
}: {
  gap?: Space;
  align?: 'start' | 'center' | 'end';
  spread?: boolean;
  end?: boolean;
  wrap?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'fsp-ui-line',
        `fsp-ui-gap-${gap}`,
        `fsp-ui-align-${align}`,
        spread && 'fsp-ui-spread',
        end && 'fsp-ui-end',
        wrap && 'fsp-ui-wrap',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Grows to take the room its line leaves, and may shrink below its content. */
export function Grow({ children }: { children: ReactNode }) {
  return <div className="fsp-ui-grow">{children}</div>;
}

/**
 * WHAT STAYS AT THE TOP OF A CARD while the rest of it scrolls under it: a
 * player's name and his two numbers, a trade's two sides. It sticks under
 * whatever already sticks there (`--fsp-stick-top`: a sheet's title bar), and
 * what comes after it in the same parent sticks under it in turn — a game
 * log's column names (UX-36) — so they need its height, which no stylesheet
 * knows: names wrap, buttons go under the words. This measures it, and hands
 * it to the stylesheet as `--fsp-pinned-h` on the parent.
 *
 * IN THE KIT because it writes a style property, which a clean screen never
 * does (LOOKS.md Part 7).
 */
export function PinnedTop({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;
    const measure = () =>
      parent.style.setProperty('--fsp-pinned-h', `${el.getBoundingClientRect().height}px`);
    measure();
    const sizes = new ResizeObserver(measure);
    sizes.observe(el);
    return () => {
      sizes.disconnect();
      parent.style.removeProperty('--fsp-pinned-h');
    };
  }, []);
  return (
    <div ref={ref} className={cx('fsp-ui-pinned-top', className)}>
      {children}
    </div>
  );
}

// ── Words ───────────────────────────────────────────────────────────────────

/**
 * Words in one of the look's sizes and weights and one of the palette's inks.
 * `nowrap` cuts a long line with an ellipsis rather than wrapping it.
 */
export function Text({
  tone = 'primary',
  size = 'sm',
  weight = 'regular',
  numeric = false,
  nowrap = false,
  block = false,
  center = false,
  mono = false,
  children,
}: {
  tone?: 'primary' | 'secondary' | 'muted' | 'good' | 'bad' | 'warn' | 'accent';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  weight?: 'regular' | 'medium' | 'strong' | 'bold';
  numeric?: boolean;
  nowrap?: boolean;
  /** A paragraph of its own rather than a run inside one. */
  block?: boolean;
  /** Centered in its line: a note under a form. */
  center?: boolean;
  /** In the look's fixed-width face: a commit, a code to type. */
  mono?: boolean;
  children: ReactNode;
}) {
  const Tag = block ? 'div' : 'span';
  return (
    <Tag
      className={cx(
        'fsp-ui-text',
        `fsp-ui-ink-${tone}`,
        `fsp-ui-size-${size}`,
        `fsp-ui-weight-${weight}`,
        numeric && 'fsp-num',
        nowrap && 'fsp-ui-nowrap',
        center && 'fsp-ui-center',
        mono && 'fsp-ui-mono',
      )}
    >
      {children}
    </Tag>
  );
}

/** A screen's own title, in the look's display face. */
export function Title({ children }: { children: ReactNode }) {
  return <h1 className="fsp-disp fsp-ui-title">{children}</h1>;
}

/** A section's heading inside a screen, a step under its `Title`. */
export function Heading({ children }: { children: ReactNode }) {
  return <h2 className="fsp-disp fsp-ui-heading">{children}</h2>;
}

/** A small capitalized label over a group: "You give", "Thread". */
export function Caption({ children }: { children: ReactNode }) {
  return <div className="fsp-cap">{children}</div>;
}

/** A tinted block that says one thing: a refusal, a warning, a done deal. */
export function Callout({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <div
      className={cx('fsp-ui-callout', `fsp-ui-tone-${tone}`)}
      role={tone === 'bad' ? 'alert' : undefined}
    >
      {children}
    </div>
  );
}

/**
 * The quiet surface a group of rows sits on. `tone="bad"` edges it in the
 * palette's loss color: the one card on a screen that cannot be undone.
 */
export function Card({
  children,
  padded = false,
  tone,
}: {
  children: ReactNode;
  padded?: boolean;
  tone?: 'bad';
}) {
  return (
    <div
      className={cx(
        'fsp-surface',
        'fsp-ui-card',
        padded && 'fsp-ui-padded',
        tone && 'fsp-ui-card-bad',
      )}
    >
      {children}
    </div>
  );
}

// ── Marks ───────────────────────────────────────────────────────────────────

const TONE_COLOR: Record<Tone, MantineColor> = {
  neutral: 'gray',
  accent: 'accent',
  good: 'green',
  warn: 'yellow',
  bad: 'red',
};

/** A status in a word: "Under review", "Done". `icon` leads it: a sport's ball. */
export function Chip({
  tone = 'neutral',
  icon,
  children,
}: {
  tone?: Tone;
  icon?: IconName;
  children: ReactNode;
}) {
  return (
    <Badge
      size="sm"
      variant="light"
      color={TONE_COLOR[tone]}
      // The accent's words, lifted to read on its own wash (UX-27): Mantine
      // draws them in the raw accent, and Miami's aqua read 3.5:1 in light.
      style={
        tone === 'accent'
          ? ({ '--badge-color': 'var(--fsp-accent-text)' } as CSSProperties)
          : undefined
      }
      className="fsp-ui-chip"
      leftSection={icon ? <Icon name={icon} size="sm" /> : undefined}
    >
      {children}
    </Badge>
  );
}

/** A small round count beside a label; nothing at zero. */
export function Count({ value, tone = 'neutral' }: { value: number; tone?: Tone }) {
  if (value <= 0) return null;
  return (
    <Badge size="xs" variant="filled" circle color={TONE_COLOR[tone]} className="fsp-ui-count">
      {value}
    </Badge>
  );
}

/** A box that reads as checked or not; the row around it is the control. */
export function Tick({ on }: { on: boolean }) {
  return <span className={cx('fsp-check', on && 'on')} aria-hidden />;
}

// ── Controls ────────────────────────────────────────────────────────────────

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'soft'
  | 'quiet'
  | 'danger'
  | 'danger-quiet'
  | 'caution'
  | 'caution-soft';

const BUTTON: Record<ButtonVariant, { variant: string; color?: MantineColor }> = {
  primary: { variant: 'filled' },
  secondary: { variant: 'default' },
  /** Tinted with the accent: a third door beside a primary and a secondary. */
  soft: { variant: 'light' },
  quiet: { variant: 'subtle' },
  danger: { variant: 'filled', color: 'red' },
  'danger-quiet': { variant: 'subtle', color: 'red' },
  caution: { variant: 'filled', color: 'yellow' },
  /** A row's claim: yellow, as a claim is marked, and tinted, as a row's action is (B-278). */
  'caution-soft': { variant: 'light', color: 'yellow' },
};

/**
 * A button. `primary` is the one next action a screen wants most (CLAUDE.md:
 * the next action the most prominent thing); everything else is quieter.
 * `to` makes it a link to a route; `loading` shows it working and takes no
 * second press; `submit` makes it its form's submit button.
 */
export function Button({
  variant = 'secondary',
  size = 'sm',
  icon,
  iconRight,
  full = false,
  disabled = false,
  loading = false,
  submit = false,
  to,
  href,
  label,
  hook,
  expanded,
  onClick,
  children,
}: {
  variant?: ButtonVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  icon?: IconName;
  iconRight?: IconName;
  full?: boolean;
  disabled?: boolean;
  loading?: boolean;
  submit?: boolean;
  to?: string;
  /**
   * A page on another site, opened in a new tab: the words on the button say
   * whose site it is, and the screen reader hears that it opens a new tab.
   */
  href?: string;
  /** What a screen reader says, when the words on the button are not enough. */
  label?: string;
  /** A bare `data-` attribute a walk or the tour finds the button by: `data-tour-next`. */
  hook?: `data-${string}`;
  /** A button that opens something in place says whether it is open (`aria-expanded`). */
  expanded?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  const v = BUTTON[variant];
  const common = {
    ...(hook ? { [hook]: '' } : {}),
    // A quiet red button's words in the palette's loss color: Mantine's own
    // light red is near-white in dark mode, so Drop and Delete read as plain
    // white there — in every look.
    // A quiet or soft one's in the accent's words, which the palette lifts to
    // 4.5:1 on the surface and on the accent's own wash (UX-27): Mantine draws
    // them in the raw accent, and a light accent on a light ground read
    // 2.5:1 to 3.7:1 (Miami's aqua on Crayon's paper, the default orange on
    // a hint's wash; the audit's run 7).
    style:
      variant === 'danger-quiet'
        ? ({
            '--button-color': 'var(--fsp-loss)',
            '--button-hover': 'color-mix(in srgb, var(--fsp-loss) 12%, transparent)',
          } as CSSProperties)
        : variant === 'quiet' || variant === 'soft'
          ? ({ '--button-color': 'var(--fsp-accent-text)' } as CSSProperties)
          : undefined,
    size,
    variant: v.variant,
    color: v.color,
    fullWidth: full,
    disabled,
    loading,
    'aria-label': label,
    ...(expanded === undefined ? {} : { 'aria-expanded': expanded }),
    leftSection: icon ? <Icon name={icon} size="sm" /> : undefined,
    rightSection: iconRight ? <Icon name={iconRight} size="sm" /> : undefined,
  };
  if (to !== undefined) {
    return (
      <MantineButton {...common} component={Link} to={to} onClick={onClick}>
        {children}
      </MantineButton>
    );
  }
  if (href !== undefined) {
    return (
      <MantineButton
        {...common}
        component="a"
        href={href}
        target="_blank"
        rel="noopener"
        // THE WORDS AND THE NEW TAB, whichever names it: `label` when given,
        // else the words on it; with neither a string, the words are its name
        // and the suffix is left off rather than said alone.
        aria-label={
          label !== undefined
            ? `${label} (opens in a new tab)`
            : typeof children === 'string'
              ? `${children} (opens in a new tab)`
              : undefined
        }
        onClick={onClick}
      >
        {children}
      </MantineButton>
    );
  }
  return (
    <MantineButton {...common} type={submit ? 'submit' : 'button'} onClick={onClick}>
      {children}
    </MantineButton>
  );
}

/**
 * More of a form behind one quiet line, closed until asked for: *More draft
 * options*, *More options* (PLAN-2026-10-18-families-and-newcomers §4.5). The
 * line says what it opens (DIRECTION U24) and says whether it is open.
 */
export function Reveal({
  label,
  hook,
  children,
}: {
  label: string;
  hook?: `data-${string}`;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Stack gap={2}>
      <div>
        <Button
          variant="quiet"
          size="xs"
          iconRight={open ? 'up' : 'expand'}
          expanded={open}
          {...(hook ? { hook } : {})}
          onClick={() => setOpen((o) => !o)}
        >
          {label}
        </Button>
      </div>
      {open ? <Stack gap={3}>{children}</Stack> : null}
    </Stack>
  );
}

/**
 * A button that is only an icon: sending a message, closing a thing, the
 * bell. `label` is what a screen reader says and a pointer's tooltip shows,
 * since there are no words on it. `submit` makes it its form's submit button.
 * `count` draws a small number on its corner — the bell's unread — and is
 * said as part of the label, since a screen reader does not see the corner.
 *
 * IT TAKES A REF, and passes on whatever else it is handed, so a popover can
 * hang its open and shut state on the button itself (`Popover`'s `button`):
 * on a wrapper around it, a screen reader heard a plain button and an
 * `aria-expanded` that belonged to nothing (UX_STANDARDS UX-32).
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  {
    icon: IconName;
    label: string;
    variant?: 'primary' | 'secondary' | 'quiet';
    submit?: boolean;
    disabled?: boolean;
    count?: number;
    /** `sm` inside a dense row; `xl` for a stepper's big minus and plus. */
    size?: 'sm' | 'lg' | 'xl';
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  } & Omit<HTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'>
>(function IconButton(
  {
    icon,
    label,
    variant = 'primary',
    submit = false,
    disabled = false,
    count,
    size = 'lg',
    onClick,
    ...rest
  },
  ref,
) {
  const said = count ? `${label}, ${count} unread` : label;
  return (
    <span className="fsp-ui-icon-button">
      <ActionIcon
        {...rest}
        ref={ref}
        type={submit ? 'submit' : 'button'}
        variant={variant === 'primary' ? 'filled' : variant === 'secondary' ? 'default' : 'subtle'}
        size={size}
        aria-label={said}
        title={said}
        disabled={disabled}
        onClick={onClick}
      >
        <Icon name={icon} size="sm" />
      </ActionIcon>
      {count ? (
        <span className="fsp-ui-corner-count" aria-hidden>
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </span>
  );
});

/** A small round mark: this row is new to me. Said by the row's own words, not by the dot. */
export function Dot({ on }: { on: boolean }) {
  return <span className={cx('fsp-ui-dot', on && 'on')} aria-hidden />;
}

/**
 * Words in a color the DATA chooses, not the look: a manager's name in their
 * team's color (CHAT.md). The one place a clean screen hands the kit a color,
 * and it goes on as a custom property so the look still decides the weight,
 * the size and whether to use it at all (`.fsp-ui-ink` in ui.css).
 */
export function Ink({ color, children }: { color: string | null; children: ReactNode }) {
  return (
    <span
      className="fsp-ui-ink"
      data-colored={color === null ? undefined : true}
      style={color === null ? undefined : ({ '--fsp-ink-data': color } as CSSProperties)}
    >
      {children}
    </span>
  );
}

/**
 * Tabs across the top of a list: one choice of several, each with an
 * optional count. A tablist, so a keyboard and a screen reader get it right.
 */
export function Segments<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: readonly { id: T; label: string; count?: number; tone?: Tone }[];
  value: T;
  onChange: (id: T) => void;
  label: string;
}) {
  return (
    <div
      className={cx('fsp-segments', `fsp-ui-cols-${items.length}`)}
      role="tablist"
      aria-label={label}
    >
      {items.map((s) => (
        <button
          key={s.id}
          type="button"
          role="tab"
          aria-selected={value === s.id}
          className="fsp-segment"
          onClick={() => onChange(s.id)}
        >
          {s.label}
          {s.count ? <Count value={s.count} tone={s.tone ?? 'neutral'} /> : null}
        </button>
      ))}
    </div>
  );
}

const activate = (run: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    run();
  }
};

/**
 * A row's or a card's own door, laid under everything in it (B-220): the
 * whole surface still answers a tap, and a name inside it that goes
 * somewhere else is its own button above this one rather than a button
 * inside a button, which is not valid HTML and which a screen reader cannot
 * tell apart. The host carries `fsp-cover-host`; `label` is what the door
 * does ("Open the matchup").
 */
export function Cover({
  label,
  onClick,
  to,
  onFollow,
}: {
  label: string;
  onClick?: () => void;
  to?: string;
  /**
   * A link's own click, before it is followed: a screen that moves to it its
   * own way (a morph, B-409) prevents the default; a modified click is left
   * to the browser.
   */
  onFollow?: (event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  return to !== undefined ? (
    <Link to={to} className="fsp-cover" aria-label={label} onClick={onFollow} />
  ) : (
    <button type="button" className="fsp-cover" aria-label={label} onClick={onClick} />
  );
}

/**
 * One row of a list, in three columns: a small leading mark, the body, and
 * whatever trails. Clickable when it has `onClick`; a checkbox when it has
 * `checked`. `lead` sets the leading column's width. With `cover` the row's
 * click is a `Cover` named by it, so a name inside can go somewhere of its own.
 */
export function ListRow({
  lead,
  trail,
  children,
  onClick,
  checked,
  selected = false,
  dim = false,
  leadSize = 'md',
  tall = false,
  cover,
}: {
  lead?: ReactNode;
  trail?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  /** What the row's click does, when the row holds a link of its own (B-220). */
  cover?: string;
  /** A checkbox row: its state, announced. */
  checked?: boolean;
  selected?: boolean;
  dim?: boolean;
  leadSize?: 'sm' | 'md' | 'lg' | 'xl';
  /** Room for two lines of body. */
  tall?: boolean;
}) {
  const clickable = onClick !== undefined && !dim;
  const covered = clickable && cover !== undefined && checked === undefined;
  const whole = clickable && !covered;
  return (
    <div
      className={cx(
        'fsp-row',
        'fsp-ui-row',
        `fsp-ui-lead-${lead === undefined ? 'none' : leadSize}`,
        trail === undefined && 'fsp-ui-no-trail',
        tall && 'fsp-ui-tall',
        clickable && 'fsp-row-clickable',
        covered && 'fsp-cover-host',
        selected && 'fsp-row-selected',
        dim && 'fsp-row-dim',
      )}
      role={checked !== undefined ? 'checkbox' : whole ? 'button' : undefined}
      aria-checked={checked}
      aria-disabled={dim || undefined}
      tabIndex={whole ? 0 : undefined}
      onClick={whole ? onClick : undefined}
      onKeyDown={whole ? activate(onClick) : undefined}
    >
      {covered ? <Cover label={cover} onClick={onClick} /> : null}
      {lead === undefined ? null : <span className="fsp-ui-row-lead">{lead}</span>}
      <div className="fsp-ui-row-body">{children}</div>
      {trail === undefined ? null : <div className="fsp-ui-row-trail">{trail}</div>}
    </div>
  );
}

/** A name on the first line of a row, cut with an ellipsis, with marks after it. */
export function RowName({ children, marks }: { children: ReactNode; marks?: ReactNode }) {
  return (
    <div className="fsp-name">
      <span className="fsp-name-text">{children}</span>
      {marks}
    </div>
  );
}

/** A row's second line: quiet, one line, cut short. `wrap` lets it run on. */
export function RowSub({ children, wrap = false }: { children: ReactNode; wrap?: boolean }) {
  return <div className={cx('fsp-sub', wrap && 'fsp-ui-wrap-text')}>{children}</div>;
}

// ── Fields ──────────────────────────────────────────────────────────────────

/** Several lines of words: a note, a reason. */
export function TextArea({
  label,
  description,
  placeholder,
  value,
  onChange,
  maxLength,
  disabled = false,
}: {
  label?: string;
  description?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  disabled?: boolean;
}) {
  return (
    <Textarea
      label={label}
      description={description}
      placeholder={placeholder}
      autosize
      minRows={1}
      maxLength={maxLength}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

/**
 * One line of words. `hideLabel` keeps the label for a screen reader and off
 * the screen, for a field whose placeholder and place already say what it is
 * (the chat composer).
 */
export function TextField({
  label,
  description,
  placeholder,
  value,
  onChange,
  maxLength,
  hideLabel = false,
  disabled = false,
  grow = false,
  type = 'text',
  autoComplete,
  error,
  autoFocus = false,
  icon,
  code = false,
  readOnly = false,
  inputMode,
  inputRef,
  onKeyDown,
  onSelect,
}: {
  label: string;
  description?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  hideLabel?: boolean;
  disabled?: boolean;
  /** Take the rest of its row, beside a button. */
  grow?: boolean;
  /** A time of day or a date and time, in the browser's own picker; an address. */
  type?: 'text' | 'time' | 'datetime-local' | 'email';
  /** What the browser may fill it with: `email`, `username`, `name`. */
  autoComplete?: string;
  /** Said under the field in the bad tone, and the field marked. */
  error?: string | null;
  /** Takes the keyboard when it appears: the first field of a sheet. */
  autoFocus?: boolean;
  /** A mark inside the field's start: a search's magnifier. */
  icon?: IconName;
  /** A code to type out: large, fixed-width and spaced, an invite code. */
  code?: boolean;
  /** Shown as a field and not editable here: the address you sign in with. */
  readOnly?: boolean;
  /** The keyboard a phone offers: digits for a jersey's number, a point too for a score. */
  inputMode?: 'numeric' | 'decimal';
  /**
   * The input itself, for a composer that puts an emoji where the cursor is
   * (PLAN-2026-10-27-more-fun-in-chat C1).
   */
  inputRef?: Ref<HTMLInputElement>;
  /** Keys before the field acts on them: a composer's suggestions take arrows and Enter. */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** The cursor moved or the selection changed. */
  onSelect?: (e: SyntheticEvent<HTMLInputElement>) => void;
}) {
  return (
    <TextInput
      ref={inputRef}
      onKeyDown={onKeyDown}
      onSelect={onSelect}
      inputMode={inputMode}
      className={cx(grow && 'fsp-ui-grow', code && 'fsp-ui-code-field') || undefined}
      size={code ? 'lg' : undefined}
      type={type}
      autoComplete={autoComplete}
      error={error ?? undefined}
      autoFocus={autoFocus}
      data-autofocus={autoFocus || undefined}
      leftSection={icon ? <Icon name={icon} size="sm" /> : undefined}
      label={hideLabel ? undefined : label}
      aria-label={hideLabel ? label : undefined}
      description={description}
      placeholder={placeholder}
      value={value}
      maxLength={maxLength}
      disabled={disabled}
      readOnly={readOnly}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

/** A password: hidden as it is typed, with a switch to show it. */
export function PasswordField({
  label,
  description,
  value,
  onChange,
  autoComplete = 'current-password',
  error,
}: {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: 'current-password' | 'new-password';
  error?: string | null;
}) {
  return (
    <PasswordInput
      label={label}
      description={description}
      value={value}
      autoComplete={autoComplete}
      error={error ?? undefined}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

/**
 * A color, as a swatch and its hex: a team's own colors. `onPicking` says
 * while its picker is open (the field has focus), so a sheet around it can
 * let Escape close the picker — by leaving the field — and not the sheet.
 */
export function ColorField({
  label,
  value,
  onChange,
  error,
  onPicking,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  onPicking?: (picking: boolean) => void;
}) {
  return (
    <ColorInput
      label={label}
      value={value}
      onChange={onChange}
      error={error ?? undefined}
      format="hex"
      withEyeDropper={false}
      popoverProps={{ withinPortal: true }}
      onFocus={() => onPicking?.(true)}
      onBlur={() => onPicking?.(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') e.currentTarget.blur();
      }}
    />
  );
}

/**
 * A box ticked or not, with its words beside it. `description` is the
 * smaller line under the words, lined up with them rather than the box:
 * what ticking it will do.
 */
export function Checkbox({
  label,
  description,
  on,
  onChange,
  disabled = false,
}: {
  label: ReactNode;
  description?: ReactNode;
  on: boolean;
  onChange: (on: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <MantineCheckbox
      label={label}
      description={description}
      checked={on}
      disabled={disabled}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  );
}

/**
 * A number. `nullable` lets it be cleared to "no limit" (null) rather than to
 * zero; `decimals` is how many places it takes; `negative` allows below zero.
 */
export function NumberField({
  label,
  description,
  value,
  onChange,
  min,
  max,
  step = 1,
  decimals = 0,
  negative = false,
  nullable = false,
  disabled = false,
  hideLabel = false,
  prefix,
}: {
  label: string;
  description?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  decimals?: number;
  negative?: boolean;
  nullable?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  /** Written before the number: "$" on a bid. */
  prefix?: string;
}) {
  return (
    <NumberInput
      prefix={prefix}
      label={hideLabel ? undefined : label}
      aria-label={hideLabel ? label : undefined}
      description={description}
      value={value ?? ''}
      onChange={(v) => {
        if (v === '' || v === null) onChange(nullable ? null : (min ?? 0));
        else onChange(Number(v));
      }}
      min={min}
      max={max}
      step={step}
      decimalScale={decimals}
      allowDecimal={decimals > 0}
      allowNegative={negative}
      disabled={disabled}
    />
  );
}

/**
 * On or off, said out loud: a notification channel, a league's mute. `label`
 * is the switch's name for a screen reader; `hideLabel` keeps it off the
 * screen when a column heading already says it.
 */
export function Toggle({
  label,
  description,
  on,
  onChange,
  disabled = false,
  hideLabel = false,
}: {
  label: string;
  /** A line under the label saying what it switches. */
  description?: string;
  on: boolean;
  onChange: (on: boolean) => void;
  disabled?: boolean;
  hideLabel?: boolean;
}) {
  return (
    <Switch
      checked={on}
      onChange={(e) => onChange(e.currentTarget.checked)}
      disabled={disabled}
      label={hideLabel ? undefined : label}
      description={hideLabel ? undefined : description}
      aria-label={hideLabel ? label : undefined}
    />
  );
}

/**
 * One of a few, all in view: a setting with a handful of stops (dark, light,
 * system). A radio group, so it reads as a setting and not as tabs.
 */
export function OneOf<T extends string>({
  label,
  options,
  value,
  onChange,
  hideLabel = false,
}: {
  label: string;
  /** `disabled` is offered and cannot be chosen: "Auction (soon)". */
  options: readonly { value: T; label: string; disabled?: boolean }[];
  value: T;
  onChange: (value: T) => void;
  hideLabel?: boolean;
}) {
  return (
    <div className="fsp-ui-field">
      {hideLabel ? null : <div className="fsp-ui-field-label">{label}</div>}
      <SegmentedControl
        aria-label={label}
        value={value}
        onChange={(v) => onChange(v as T)}
        data={options.map((o) => ({ value: o.value, label: o.label, disabled: o.disabled }))}
        fullWidth
      />
    </div>
  );
}

/**
 * Where along something that plays: a replay's picks (C11). From 0 to `max`
 * in whole steps; `marks` are the places worth finding again, drawn as ticks
 * along it (my own picks). `label` is what a screen reader calls it and
 * `said` how it says a place ("Pick 28 of 180"), since a bare number means
 * nothing heard. No bubble over the thumb: the screen beside it says where.
 */
export function Scrubber({
  label,
  value,
  max,
  onChange,
  marks = [],
  said,
}: {
  label: string;
  value: number;
  max: number;
  onChange: (value: number) => void;
  marks?: readonly number[];
  said: (value: number) => string;
}) {
  return (
    <Slider
      className="fsp-ui-scrubber"
      min={0}
      max={Math.max(1, max)}
      step={1}
      value={value}
      onChange={onChange}
      marks={marks.map((m) => ({ value: m }))}
      label={null}
      thumbLabel={label}
      thumbProps={{ 'aria-valuetext': said(value) }}
      disabled={max <= 0}
    />
  );
}

/** An option of a `Choice`: `disabled` is offered and cannot be chosen ("Auction (soon)"). */
export interface ChoiceOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * One of a list. Searchable by default, for a long list (a player, a stat);
 * `searchable={false}` for a handful. Options may come in named groups.
 * `required` means it cannot be cleared back to nothing.
 */
export function Choice({
  label,
  description,
  placeholder,
  options,
  value,
  onChange,
  empty = 'Nothing matches.',
  searchable = true,
  required = false,
  clearable = false,
  disabled = false,
  hideLabel = false,
}: {
  label: string;
  description?: string;
  placeholder?: string;
  /** A small ✕ that empties it back to the placeholder. */
  clearable?: boolean;
  options: ChoiceOption[] | { group: string; items: ChoiceOption[] }[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** What an empty search says. */
  empty?: string;
  searchable?: boolean;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
}) {
  return (
    <Select
      label={hideLabel ? undefined : label}
      aria-label={hideLabel ? label : undefined}
      description={description}
      placeholder={placeholder}
      data={options}
      value={value}
      onChange={onChange}
      searchable={searchable}
      clearable={clearable}
      allowDeselect={!required}
      disabled={disabled}
      nothingFoundMessage={empty}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

/** Several of a list, as a field: the positions a rule applies to, weeks. */
export function MultiChoice({
  label,
  placeholder,
  options,
  value,
  onChange,
  disabled = false,
  hideLabel = false,
}: {
  label: string;
  placeholder?: string;
  options: ChoiceOption[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  hideLabel?: boolean;
}) {
  return (
    <MultiSelect
      label={hideLabel ? undefined : label}
      aria-label={hideLabel ? label : undefined}
      placeholder={value.length === 0 ? placeholder : undefined}
      data={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

/**
 * A few choices laid out as chips, each on or off — or, with `single`, one of
 * them: a preset, the awards a league hands out.
 */
export function Chips({
  label,
  options,
  value,
  onChange,
  single = false,
  clearable = false,
  disabled = false,
}: {
  label: string;
  options: readonly { value: string; label: string; hint?: string }[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  single?: boolean;
  /** One at a time, and the chosen one pressed again chooses none: every position (B-511). */
  clearable?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="fsp-ui-chips" role="group" aria-label={label}>
      {options.map((o) => (
        <MantineChip
          key={o.value}
          // Black or white words on a chosen chip, whichever reads (B-277):
          // Mantine works the contrast out only for a chip given its color.
          autoContrast
          color="accent"
          size="xs"
          checked={value.includes(o.value)}
          disabled={disabled}
          title={o.hint}
          onChange={(on) => {
            if (single || clearable) onChange(on ? [o.value] : clearable ? [] : [...value]);
            else onChange(on ? [...value, o.value] : value.filter((v) => v !== o.value));
          }}
        >
          {o.label}
        </MantineChip>
      ))}
    </div>
  );
}

/** A question in its own box over the screen: a reason, a confirmation. Esc and the backdrop close it. */
export function Dialog({
  opened,
  onClose,
  title,
  children,
}: {
  opened: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <>
      <BackCloses opened={opened} onClose={onClose} />
      <Modal
        opened={opened}
        onClose={onClose}
        title={title}
        centered
        // Its × is named, as the kit's sheets' are (UX_STANDARDS UX-30).
        closeButtonProps={{ 'aria-label': 'Close' }}
      >
        {children}
      </Modal>
    </>
  );
}

/**
 * Numbers in rows and columns, head first. `numeric` marks the columns that
 * line up on their figures; a `strong` row is the reader's own.
 */
export function Table({
  head,
  rows,
  numeric = [],
  label,
}: {
  head: readonly ReactNode[];
  rows: readonly { key: string; cells: readonly ReactNode[]; strong?: boolean }[];
  numeric?: readonly number[];
  label: string;
}) {
  return (
    <table className="fsp-ui-table" aria-label={label}>
      <thead>
        <tr>
          {head.map((h, i) => (
            <th key={i} className={numeric.includes(i) ? 'fsp-num' : undefined} scope="col">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className={r.strong ? 'fsp-ui-table-strong' : undefined}>
            {r.cells.map((c, i) => (
              <td key={i} className={numeric.includes(i) ? 'fsp-num' : undefined}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Telling somebody ────────────────────────────────────────────────────────

/**
 * What just happened, with a way to take it back: an add, a drop. Stays a
 * little longer than a plain message, and says how the undo went.
 */
export function tellWithUndo(
  message: string,
  undo: () => string | null | Promise<string | null>,
  tone: 'good' | 'bad' = 'good',
): void {
  const id = `undo-${Date.now()}`;
  notifications.show({
    id,
    color: TONE_COLOR[tone],
    autoClose: 8000,
    message: (
      <span className="fsp-ui-undo">
        <span>{message}</span>
        <MantineButton
          size="compact-xs"
          variant="white"
          leftSection={<Icon name="undo" size="sm" />}
          onClick={() => {
            notifications.hide(id);
            void Promise.resolve(undo()).then((err) =>
              notifications.show({
                message: err ?? 'Undone.',
                color: err ? 'red' : 'gray',
                autoClose: 2500,
              }),
            );
          }}
        >
          Undo
        </MantineButton>
      </span>
    ),
  });
}

/** A short message that comes and goes: what just happened, or why it did not. */
export function tell(message: string, tone: Tone = 'neutral'): void {
  notifications.show({
    message,
    color: TONE_COLOR[tone],
    autoClose: tone === 'bad' ? 6000 : 3000,
  });
}

// ── Measures and colors the data chooses ────────────────────────────────────

/** How far along something is, 0 to 1, as a bar: a badge's progress. */
export function Meter({ value, label }: { value: number; label?: string }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <span
      className="fsp-ui-meter"
      role={label ? 'meter' : undefined}
      aria-label={label}
      aria-valuenow={label ? pct : undefined}
      aria-valuemin={label ? 0 : undefined}
      aria-valuemax={label ? 100 : undefined}
      aria-hidden={label ? undefined : true}
    >
      <span className="fsp-ui-meter-fill" style={{ width: `${pct}%` }} />
    </span>
  );
}

/**
 * A block wearing colors the DATA chooses — a team's wash behind its award,
 * its stripe on a row — handed over as custom properties so the look decides
 * how, and whether, to use them (the `Ink` rule, for a block). Only colors go
 * in `colors`; everything else a look draws is its own.
 */
export function Colored({
  colors,
  className,
  children,
  inline = false,
  ...rest
}: {
  colors: Record<`--${string}`, string>;
  className?: string;
  /** A span rather than a div: inside a button, or a run of words. */
  inline?: boolean;
  children?: ReactNode;
  role?: string;
  tabIndex?: number;
  title?: string;
  'aria-modal'?: boolean;
  'aria-label'?: string;
  /** State a stylesheet reads: `data-tone`, `data-current`. */
  [data: `data-${string}`]: string | undefined;
  onClick?: () => void;
  onKeyDown?: (e: KeyboardEvent) => void;
}) {
  const Tag = inline ? 'span' : 'div';
  return (
    <Tag className={className} style={colors as CSSProperties} {...rest}>
      {children}
    </Tag>
  );
}

/** A face an account wears, on its disc's two colors (PLAN-2026-10-24-faces-round-three §7). */
export interface WornFace {
  face: Face;
  colors: { primary: string; secondary: string };
}

/**
 * A person's initial on their own color — the account menu's button — or the
 * face they made, on its disc. The color is the account's (chosen from its
 * seed), handed to the look as data. Forwards its ref, so a menu can open
 * from it.
 */
export const Avatar = forwardRef<
  HTMLButtonElement,
  {
    name: string;
    color: string;
    label: string;
    /** The account's face, drawn in place of the initial. */
    face?: WornFace | null;
    className?: string;
    onClick?: () => void;
  }
>(function Avatar({ name, color, label, face, className, ...rest }, ref) {
  // A menu opening from it hands over its own props, a class among them; the
  // avatar's class is kept beside it rather than replaced.
  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      className={cx('fsp-avatar', face && 'fsp-avatar-face', className)}
      aria-label={label}
      style={{ '--fsp-avatar': color } as CSSProperties}
    >
      {face ? (
        <FaceArt face={face.face} disc={face.colors} />
      ) : (
        name.slice(0, 1).toUpperCase() || '?'
      )}
    </button>
  );
});

/**
 * A person's mark and nothing to press: their initial on their color, beside
 * their name. Decorative, since the name beside it says who.
 */
export function AvatarMark({
  name,
  color,
  face,
  size = 'md',
}: {
  name: string;
  color: string;
  /** The account's face, drawn in place of the initial. */
  face?: WornFace | null;
  /** `sm` beside a line of chat or a name in a list (B-525). */
  size?: 'sm' | 'md' | 'xl';
}) {
  return (
    <span
      className={cx('fsp-avatar', face && 'fsp-avatar-face', `fsp-ui-avatar-${size}`)}
      aria-hidden
      style={{ '--fsp-avatar': color } as CSSProperties}
    >
      {face ? (
        <FaceArt face={face.face} disc={face.colors} />
      ) : (
        name.slice(0, 1).toUpperCase() || '?'
      )}
    </span>
  );
}

/**
 * One share against another as a bar: a matchup's win chance, each side in its
 * team's color (data, as with `Ink`), the left side `share` of the width.
 */
export function SplitBar({
  share,
  left,
  right,
  label,
}: {
  share: number;
  left: string;
  right: string;
  label?: string;
}) {
  const pct = Math.round(Math.max(0, Math.min(1, share)) * 100);
  return (
    // Each side's color is data (`--fsp-left`, `--fsp-right`); a look decides
    // whether a side is a flat fill or crayon hatching.
    <div
      className="fsp-winbar"
      role={label ? 'img' : undefined}
      aria-label={label}
      style={{ '--fsp-left': left, '--fsp-right': right } as CSSProperties}
    >
      <div className="fsp-winbar-share" style={{ width: `${pct}%` }} />
      <div className="fsp-winbar-rest" />
    </div>
  );
}
